/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import {
  Fragment,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { useSharedTenderQuestions } from '~src/hooks/api-hooks/useSharedTenderQuestions';
import QuestionsList, {
  PaginationParams,
  QuestionsListProps,
} from '~components/atoms/Questions/QuestionsList';
import {
  QUESTIONS_DEFAULT_PAGE_SIZE,
  LETTER_DEFAULT_PAGE_SIZE,
} from '~src/config';
import QuestionsFilter, {
  QuestionsFilterOptions,
} from '~components/atoms/Questions/QuestionsFilter/QuestionsFilter';
import { useSharedTenderMyQuestions } from '~src/hooks/api-hooks/useSharedTendersMyQuestions';
import IconButton from '~components/_common/IconButton/IconButton';
import QuestionsAnswerUpperBlock from '~components/molecules/Tabs/TenderTabs/components/Questions/QuestionsAnswerUpperBlock';
import QuestionsAnswerDateBlock from '~components/molecules/Tabs/TenderTabs/components/Questions/QuestionsAnswerDateBlock';
import { Button, Col, Pagination, Radio, Row } from 'antd';
import { useSharedTenderDetails } from '~src/hooks/api-hooks/useTenderDetails';
import { filterByQuery } from '~src/utils/searchFilters';
import { SearchContext } from '~src/contexts/SearchContext';
import { useQuery } from 'react-query';
import { getAllMessagesVendors } from '~src/services/api/movements';
import TenderCorrectionLettersList from '~components/molecules/Tabs/TenderTabs/components/Questions/TenderCorrectionLettersList';
import { CorrelationLetterModal } from '~components/popups-modals/MovementsModals/CorrelationLetter.modal';
import { OffersMessagesModal } from '~components/popups-modals/OffersTabModals/OffersMessagesModal';
import { useDynamicHeightAndLockScroll } from '~src/hooks/useDynamicHeightAndLockScroll';
import CopyQAModal from '~components/popups-modals/CopyQAModal/CopyQAModal';
import { CopyOutlined } from '@ant-design/icons';

type QuestionsPageLayoutBlockProps = {
  shareKey?: string;
  shareId: string;
  onAddQuestion?: () => void;
};

const QuestionsPageLayoutBlock: React.FC<QuestionsPageLayoutBlockProps> = ({
  shareId,
  shareKey,
  onAddQuestion,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const history = useHistory();
  const { query } = useContext(SearchContext);

  const { data: tenderDetails } = useSharedTenderDetails(
    { id: shareId, shareKey },
    { enabled: !!shareKey }
  );

  // STATE FILTER OPTIONS
  const [currentFilterOption, setCurrentFilterOption] = useState(
    QuestionsFilterOptions.ALL
  );
  // STATE FOR LETTERS ---
  const [messageTypeFilter, setMessageTypeFilter] = useState<
    'all' | '2' | '5' | '6'
  >('all');
  const [selectedLetterForView, setSelectedLetterForView] = useState<any>(null);
  const [selectedEvalMsgForView, setSelectedEvalMsgForView] = useState<any>(
    null
  );
  const [copyQAModalVisible, setCopyQAModalVisible] = useState(false);
  const [
    messagesPagination,
    setMessagesPagination,
  ] = useState<PaginationParams>({
    current: 1,
    pageSize: LETTER_DEFAULT_PAGE_SIZE,
    hasMore: false,
    total: 0,
  });

  const [qaDaysLeft, setQaDaysLeft] = useState<number | undefined>();
  const isOnTender = true;

  const {
    containerRef,
    dynamicHeight,
  } = useDynamicHeightAndLockScroll<HTMLDivElement>();

  const [
    allQuestionsPagination,
    setAllQuestionsPagination,
  ] = useState<PaginationParams>({
    current: 1,
    pageSize: QUESTIONS_DEFAULT_PAGE_SIZE,
    hasMore: false,
    total: 0,
  });
  const [
    myQuestionsPagination,
    setMyQuestionsPagination,
  ] = useState<PaginationParams>({
    current: 1,
    pageSize: QUESTIONS_DEFAULT_PAGE_SIZE,
    hasMore: false,
    total: 0,
  });

  const {
    data: allQuestionsData,
    isFetching: isAllQuestionsFetching,
    refetch: refetchAllQuestions,
  } = useSharedTenderQuestions(
    {
      share_id: shareId,
      share_key: shareKey,
      skip:
        (allQuestionsPagination.current - 1) * allQuestionsPagination.pageSize,
      take: allQuestionsPagination.pageSize,
    },
    {
      onSuccess: (data) => {
        setAllQuestionsPagination((prev) => ({
          ...prev,
          hasMore:
            allQuestionsPagination.total >
            allQuestionsPagination.current * allQuestionsPagination.pageSize,
          total: data.totalCount,
        }));
      },
      enabled: false,
    }
  );

  const {
    data: myQuestionsData,
    isFetching: isMyQuestionsFetching,
    refetch: refetchMyQuestions,
  } = useSharedTenderMyQuestions(
    {
      share_id: shareId,
      share_key: shareKey,
      skip:
        (myQuestionsPagination.current - 1) * myQuestionsPagination.pageSize,
      take: myQuestionsPagination.pageSize,
    },
    {
      onSuccess: (data) => {
        setMyQuestionsPagination((prev) => ({
          ...prev,
          hasMore:
            myQuestionsPagination.total >
            myQuestionsPagination.current * myQuestionsPagination.pageSize,
          total: data.totalCount,
        }));
      },
      enabled: false,
    }
  );

  const messagesQuery = useQuery(
    ['shared_messages', shareId, messageTypeFilter],
    () =>
      getAllMessagesVendors({
        shareId,
        shareKey: shareKey!,
        sort: 'desc(sent_at)',
        type:
          messageTypeFilter === 'all' ? undefined : [Number(messageTypeFilter)],
      }),
    {
      enabled:
        currentFilterOption === QuestionsFilterOptions.LETTERS && !!shareKey,
      onSuccess: (data) => {
        setMessagesPagination((prev) => ({
          ...prev,
          total: data?.length || 0,
        }));
      },
    }
  );

  // Search filter
  const filteredAllQuestions = useMemo(() => {
    const items = allQuestionsData?.items || [];
    if (!query) return items;
    return items.filter(
      (q: any) =>
        filterByQuery(q.question, query) ||
        (q.answers || []).some((a: any) => filterByQuery(a.answer, query))
    );
  }, [allQuestionsData?.items, query]);

  const filteredMyQuestions = useMemo(() => {
    const items = myQuestionsData?.items || [];
    if (!query) return items;
    return items.filter(
      (q: any) =>
        filterByQuery(q.question, query) ||
        (q.answers || []).some((a: any) => filterByQuery(a.answer, query))
    );
  }, [myQuestionsData?.items, query]);

  // --- SEARCH FILTER FOR MESSAGES ---
  const filteredMessages = useMemo(() => {
    const items = messagesQuery.data || [];
    if (!query) return items;
    return items.filter(
      (msg: any) =>
        filterByQuery(msg.title, query) || filterByQuery(msg.description, query)
    );
  }, [messagesQuery.data, query]);

  // --- PAGINATED MESSAGES ---
  const paginatedMessages = useMemo(() => {
    const start =
      (messagesPagination.current - 1) * messagesPagination.pageSize;
    return filteredMessages.slice(start, start + messagesPagination.pageSize);
  }, [
    filteredMessages,
    messagesPagination.current,
    messagesPagination.pageSize,
  ]);

  const handlePaginationChange = (page: number, pageSize?: number) => {
    if (currentFilterOption === QuestionsFilterOptions.ALL) {
      setAllQuestionsPagination((prev) => ({
        ...prev,
        current: page,
        pageSize: pageSize || prev.pageSize,
      }));
    } else if (currentFilterOption === QuestionsFilterOptions.MY) {
      setMyQuestionsPagination((prev) => ({
        ...prev,
        current: page,
        pageSize: pageSize || prev.pageSize,
      }));
    } else if (currentFilterOption === QuestionsFilterOptions.LETTERS) {
      setMessagesPagination((prev) => ({
        ...prev,
        current: page,
        pageSize: pageSize || prev.pageSize,
      }));
    }
  };

  const header = (
    <div style={{ marginBottom: '20px' }}>
      <QuestionsAnswerUpperBlock isShareSide={true} />
      <div
        style={{
          marginTop: '14px',
          display: 'flex',
          justifyContent:
            currentFilterOption === QuestionsFilterOptions.LETTERS
              ? 'space-between'
              : 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 16,
        }}>
        {/* MESSAGE TYPE FILTER */}
        {currentFilterOption === QuestionsFilterOptions.LETTERS && (
          <Radio.Group
            value={messageTypeFilter}
            onChange={(e) => {
              setMessageTypeFilter(e.target.value);
              setMessagesPagination((prev) => ({ ...prev, current: 1 }));
            }}>
            <Radio value="all">{intl.formatMessage({ id: 'all' })}</Radio>
            <Radio value="2">
              {intl.formatMessage({ id: 'correction-letters' })}
            </Radio>
            <Radio value="5">
              {intl.formatMessage({ id: 'instant-messages-filter' })}
            </Radio>
            <Radio value="6">
              {intl.formatMessage({ id: 'evaluation-instant-messages-filter' })}
            </Radio>
          </Radio.Group>
        )}

        {/* COPY Q&A BUTTON - Only show for Q&A tabs */}
        {currentFilterOption !== QuestionsFilterOptions.LETTERS && (
          <Button
            icon={<CopyOutlined rev={''} />}
            onClick={() => setCopyQAModalVisible(true)}
            disabled={
              (currentFilterOption === QuestionsFilterOptions.ALL &&
                !filteredAllQuestions.length) ||
              (currentFilterOption === QuestionsFilterOptions.MY &&
                !filteredMyQuestions.length)
            }>
            {intl.formatMessage({ id: 'qa-copy-modal-title' })}
          </Button>
        )}

        <QuestionsFilter
          currentFilterOption={currentFilterOption}
          onCurrentFilterOptionChange={setCurrentFilterOption}
        />
      </div>
    </div>
  );

  useEffect(() => {
    if (currentFilterOption === QuestionsFilterOptions.ALL) {
      refetchAllQuestions();
    } else if (currentFilterOption === QuestionsFilterOptions.MY) {
      refetchMyQuestions();
    } else if (currentFilterOption === QuestionsFilterOptions.LETTERS) {
      messagesQuery.refetch();
    }
  }, [
    currentFilterOption,
    allQuestionsPagination.current,
    allQuestionsPagination.pageSize,
    myQuestionsPagination.current,
    myQuestionsPagination.pageSize,
  ]);

  // change tab to MY questions after creating new question
  useEffect(() => {
    const handleQuestionCreated = () => {
      setCurrentFilterOption(QuestionsFilterOptions.MY);
      setMyQuestionsPagination((prev) => ({ ...prev, current: 1 }));
    };

    document.addEventListener('tenderQuestionCreated', handleQuestionCreated);
    return () => {
      document.removeEventListener(
        'tenderQuestionCreated',
        handleQuestionCreated
      );
    };
  }, []);

  return (
    <Fragment>
      <div
        ref={containerRef}
        css={styles.mainWrapper}
        style={{ height: dynamicHeight }}>
        <Row gutter={[32, 24]} css={styles.contentRow} style={{ margin: 0 }}>
          <Col xs={24} lg={16} xl={18} css={styles.questionsColumn}>
            <div css={styles.questionsListContainer}>
              {header}

              <div css={styles.listContentArea}>
                {currentFilterOption === QuestionsFilterOptions.ALL && (
                  <div css={styles.scrollableList}>
                    <QuestionsList
                      questions={filteredAllQuestions}
                      isLoading={isAllQuestionsFetching}
                      pagination={allQuestionsPagination}
                      split={false}
                      handlePaginationChange={handlePaginationChange}
                      hideInternalPagination={true}
                      isOnTender={isOnTender}
                    />
                  </div>
                )}
                {currentFilterOption === QuestionsFilterOptions.MY && (
                  <div css={styles.scrollableList}>
                    <QuestionsList
                      questions={filteredMyQuestions}
                      isLoading={isMyQuestionsFetching}
                      pagination={myQuestionsPagination}
                      split={false}
                      handlePaginationChange={handlePaginationChange}
                      hideInternalPagination={true}
                      questionItemConfig={{
                        withStatus: true,
                      }}
                      isOnTender={isOnTender}
                    />
                  </div>
                )}
                {currentFilterOption === QuestionsFilterOptions.LETTERS && (
                  <div css={styles.scrollableList}>
                    <TenderCorrectionLettersList
                      projectId={0}
                      tenderId={shareId}
                      letters={paginatedMessages}
                      isLoading={messagesQuery.isFetching}
                      onViewLetter={(l) =>
                        l.type === 6
                          ? setSelectedEvalMsgForView(l)
                          : setSelectedLetterForView(l)
                      }
                      isOnTender={isOnTender}
                      shareId={shareId}
                      shareKey={shareKey}
                    />
                  </div>
                )}

                {currentFilterOption === QuestionsFilterOptions.ALL &&
                  !isAllQuestionsFetching &&
                  filteredAllQuestions.length > 0 && (
                    <div css={styles.paginationFooter}>
                      <Pagination
                        current={allQuestionsPagination.current}
                        pageSize={allQuestionsPagination.pageSize}
                        total={allQuestionsPagination.total}
                        showSizeChanger
                        pageSizeOptions={['5', '10', '20', '50']}
                        onChange={handlePaginationChange}
                      />
                    </div>
                  )}

                {currentFilterOption === QuestionsFilterOptions.MY &&
                  !isMyQuestionsFetching &&
                  filteredMyQuestions.length > 0 && (
                    <div css={styles.paginationFooter}>
                      <Pagination
                        current={myQuestionsPagination.current}
                        pageSize={myQuestionsPagination.pageSize}
                        total={myQuestionsPagination.total}
                        showSizeChanger
                        pageSizeOptions={['5', '10', '20', '50']}
                        onChange={handlePaginationChange}
                      />
                    </div>
                  )}

                {currentFilterOption === QuestionsFilterOptions.LETTERS &&
                  !messagesQuery.isFetching &&
                  filteredMessages.length > 0 && (
                    <div css={styles.paginationFooter}>
                      <Pagination
                        current={messagesPagination.current}
                        pageSize={messagesPagination.pageSize}
                        total={filteredMessages.length}
                        showSizeChanger
                        pageSizeOptions={['5', '10', '20', '50']}
                        onChange={handlePaginationChange}
                      />
                    </div>
                  )}
              </div>
            </div>
          </Col>

          <Col xs={24} lg={8} xl={6} css={styles.dateColumn}>
            <div css={styles.dateBlockWrapper}>
              <QuestionsAnswerDateBlock
                qaPeriodStart={tenderDetails?.qa_start}
                qaPeriodEnd={tenderDetails?.qa_stop}
                qaOn={tenderDetails?.qa_on || false}
                qaActiveFrom={tenderDetails?.active_from}
                qaDeadlineAt={tenderDetails?.deadline_at}
                onDaysLeftChange={setQaDaysLeft}
                registrationState={tenderDetails?.state}
                isVendor={true}
                onAddQuestion={onAddQuestion}
              />
            </div>
          </Col>
        </Row>
      </div>

      <CorrelationLetterModal
        visible={!!selectedLetterForView}
        onCancel={() => setSelectedLetterForView(null)}
        projectId={0}
        tenderId={0}
        readOnly={true}
        readOnlyData={selectedLetterForView}
        shareId={shareId}
        shareKey={shareKey}
      />

      <OffersMessagesModal
        visible={!!selectedEvalMsgForView}
        onCancel={() => setSelectedEvalMsgForView(null)}
        projectId={0}
        tenderId={0}
        readOnly={true}
        readOnlyData={selectedEvalMsgForView}
        shareId={shareId}
        shareKey={shareKey}
      />

      <CopyQAModal
        visible={copyQAModalVisible}
        onCancel={() => setCopyQAModalVisible(false)}
        questions={
          currentFilterOption === QuestionsFilterOptions.MY
            ? myQuestionsData?.items || []
            : allQuestionsData?.items || []
        }
        isVendorSide={true}
        tenderId={shareId}
        shareId={shareId}
        shareKey={shareKey}
        vendorQuestionType={
          currentFilterOption === QuestionsFilterOptions.MY ? 'my' : 'all'
        }
      />
    </Fragment>
  );
};

export default QuestionsPageLayoutBlock;

export const styleFn: ThemedStyleFn = (theme) => ({
  fragrentCss: {},
  mainWrapper: {
    width: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    minHeight: 0,
    boxSizing: 'border-box',
  },
  contentRow: {
    flex: 1,
    height: '100%',
    minHeight: 0,
  },
  questionsColumn: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  },
  dateColumn: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  },
  questionsListContainer: {
    maxWidth: '900px',
    paddingTop: '30px',
    paddingLeft: '20px',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    minHeight: 0,
    boxSizing: 'border-box',
  },
  listContentArea: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  scrollableList: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    paddingRight: '6px',
    marginBottom: '10px',
    '&::-webkit-scrollbar': { width: '6px' },
    '&::-webkit-scrollbar-thumb': {
      background: '#d9d9d9',
      borderRadius: '10px',
    },
  },
  dateBlockWrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    minHeight: 0,
    overflowY: 'auto',
    gap: '25px',
    justifyContent: 'flex-start',
    '@media (min-width: 992px)': { padding: '50px 0px 0px 0px' },
    '@media (max-width: 991px)': { padding: '0px', marginTop: '20px' },
  },
  paginationFooter: {
    flex: '0 0 auto',
    display: 'flex',
    justifyContent: 'center',
    padding: '20px 0',
    borderTop: '1px solid #f0f0f0',
  },
});
